import { useBusinessRelationshipList } from '@/entities/business-relationship/hooks/useBusinessRelationships';
import type {
  PerformanceClinic,
  TrendComparison,
} from '@/entities/performance/api/performance.api';
import {
  useClinicExcelExport,
  useClinicTrend,
  useMonthlySummary,
} from '@/entities/performance/hooks/usePerformanceQueries';
import {
  formatChange,
  formatRatio,
  monthOf,
} from '@/entities/performance/lib/performance';
import { getApiErrorCode, isNetworkError } from '@/shared/api/http-client';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { Icon } from '@/shared/components/ui/icon';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { cn } from '@/shared/lib/utils';
import { formatWon } from '@/shared/utils/format';
import { toastApi } from '@/shared/utils/toast';
import { useMemo, type ReactNode } from 'react';
import { deltaClass, downloadFile } from '../lib';
import {
  ChartLegend,
  PerformanceChart,
  type ChartMonth,
} from './PerformanceChart';

/** 라벨 · 값 한 줄 (Figma 실적 행: 14 Medium 회색 라벨 · 16 값) */
const AmountRow = ({
  label,
  value,
  strong,
  delta,
}: {
  label: string;
  value: ReactNode;
  strong?: boolean;
  delta?: number | null;
}) => (
  <div className="flex items-center justify-between gap-2">
    <span className="text-body-14-m whitespace-nowrap text-fg-tertiary">
      {label}
    </span>
    <span className="flex items-center gap-2">
      {delta !== undefined && (
        <span className={cn('text-body-16-r', deltaClass(delta))}>
          {formatChange(delta)}
        </span>
      )}
      <span
        className={cn(
          'whitespace-nowrap',
          strong
            ? 'text-body-16-sb text-fg-primary'
            : 'text-body-16-r text-fg-secondary',
        )}
      >
        {value}
      </span>
    </span>
  </div>
);

const Group = ({
  head,
  children,
}: {
  head: ReactNode;
  children: ReactNode;
}) => (
  <div className="flex items-start gap-6">
    {head}
    <div className="flex min-w-0 flex-1 flex-col justify-center gap-2">
      {children}
    </div>
  </div>
);

const Caption = ({ children }: { children: ReactNode }) => (
  <p className="w-[62px] shrink-0 text-body-14-sb text-fg-tertiary">
    {children}
  </p>
);

const comparisonRows = (
  kind: 'avg' | 'prev',
  rx?: TrendComparison,
  st?: TrendComparison,
) =>
  kind === 'avg' ? (
    <>
      <AmountRow
        label="평균 예상 금액"
        value={formatWon(rx?.previous5MonthsAverage)}
        delta={rx?.previous5MonthsChangeRate ?? null}
      />
      <AmountRow
        label="평균 확정 금액"
        value={formatWon(st?.previous5MonthsAverage)}
        delta={st?.previous5MonthsChangeRate ?? null}
        strong
      />
    </>
  ) : (
    <>
      <AmountRow
        label="예상 금액"
        value={formatWon(rx?.previousMonthAmount)}
        delta={rx?.previousMonthChangeRate ?? null}
      />
      <AmountRow
        label="확정 금액"
        value={formatWon(st?.previousMonthAmount)}
        delta={st?.previousMonthChangeRate ?? null}
        strong
      />
    </>
  );

/**
 * M-09 병의원 상세 위 카드 (Figma 712:3057)
 * - 정보: 종별 배지 · 병의원명 · 요양기관번호 · 전화번호 · 주소, [내역 내려받기](선택 병의원 · 정산월 엑셀)
 * - 병의원 월별 추세: 실적 그래프(호버 툴팁), 선택 정산월 강조
 * - 처방 · 정산 금액, 직전 5개월 · 전 월 대비
 * 요양기관번호 · 전화번호는 실적 API에 없어 거래처 목록(내 거래처)에서 찾는다. 없으면 "-".
 */
export const ClinicSummary = ({
  ym,
  clinicId,
  clinic,
  clinicLoading,
}: {
  ym: string;
  clinicId: number;
  clinic?: PerformanceClinic;
  clinicLoading: boolean;
}) => {
  const monthly = useMonthlySummary(ym, clinicId);
  const trend = useClinicTrend(ym, clinicId);
  const relationships = useBusinessRelationshipList();
  const excel = useClinicExcelExport();
  const relationship = relationships.data?.items.find(
    (r) => r.clinicId === clinicId,
  );

  const chartMonths = useMemo<ChartMonth[]>(
    () =>
      (trend.data?.months ?? []).map((m) => ({
        month: monthOf(m.settlementYearMonth),
        estimated: m.estimatedAmount,
        confirmed: m.confirmedAmount > 0 ? m.confirmedAmount : null,
      })),
    [trend.data],
  );
  // 그래프는 선택 정산월의 해 — 지난해면 12월까지, 올해면 선택 달까지 조회 가능
  const lastMonth = Math.max(monthOf(ym), ...chartMonths.map((m) => m.month));

  const exportExcel = () =>
    excel.mutate(
      { month: ym, clinicId },
      {
        onSuccess: ({ downloadUrl, fileName }) =>
          downloadFile(downloadUrl, fileName),
        onError: (error) => {
          // 네트워크 오류는 http-client가 공통 스낵바를 띄운다
          if (isNetworkError(error)) return;
          if (getApiErrorCode(error) === 'WEB_PERFORMANCE_EXPORT_EMPTY') {
            toastApi.info('내보낼 내역이 없습니다.');
            return;
          }
          toastApi.error('엑셀을 만들지 못했어요. 잠시 후 다시 시도해주세요.');
        },
      },
    );

  const s = monthly.data;
  const typeLabel = clinic?.clinicTypeLabel ?? relationship?.clinicTypeLabel;

  return (
    <section className="flex flex-col gap-4 rounded-20 bg-surface-white">
      {/* 정보 */}
      <div className="flex flex-col gap-4 p-6 pb-0">
        <div className="flex items-start justify-between gap-4">
          <div className="flex min-w-0 flex-col gap-2">
            {typeLabel && (
              <Badge
                state={typeLabel === '병원급' ? 'success' : 'default'}
                className="self-start"
              >
                {typeLabel}
              </Badge>
            )}
            {clinicLoading && !clinic ? (
              <Skeleton className="h-[30px] w-64" />
            ) : (
              <h2 className="text-heading-24-b break-keep text-fg-primary">
                {clinic?.clinicName ?? relationship?.clinicName ?? '-'}
              </h2>
            )}
          </div>
          <Button
            variant="grey"
            leftIcon={<Icon name="download-line-24" />}
            loading={excel.isPending}
            onClick={exportExcel}
            className="shrink-0"
          >
            내역 내려받기
          </Button>
        </div>
        <dl className="flex flex-wrap gap-x-10 gap-y-1 text-body-14-m">
          {[
            ['요양기관번호', relationship?.ykiho],
            ['전화번호', relationship?.phoneNumber],
            ['주소', clinic?.address ?? relationship?.address],
          ].map(([label, value]) => (
            <div key={label} className="flex min-w-0 items-center gap-2">
              <dt className="shrink-0 text-fg-tertiary">{label}</dt>
              <dd className="min-w-0 text-fg-primary">{value || '-'}</dd>
            </div>
          ))}
        </dl>
      </div>

      <div aria-hidden className="mt-2 h-px bg-line-grey" />

      <div className="flex flex-col gap-6 p-6 pt-2">
        {/* 병의원 월별 추세 */}
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <h3 className="text-subtitle-18-b text-fg-primary">
              병의원 월별 추세
            </h3>
            <ChartLegend />
          </div>
          {trend.isPending ? (
            <Skeleton className="h-[224px] w-full" />
          ) : (
            <div className="overflow-x-auto">
              <PerformanceChart
                className="min-w-[600px]"
                months={chartMonths}
                lastMonth={lastMonth}
                selectedMonth={monthOf(ym)}
              />
            </div>
          )}
        </div>

        {/* 처방 · 정산 | 직전 5개월 · 전 월 대비 */}
        <div className="flex flex-col gap-6 lg:flex-row">
          <div className="flex min-w-0 flex-1 flex-col gap-6">
            <Group
              head={
                <Badge state="default" size="medium">
                  처방
                </Badge>
              }
            >
              <AmountRow
                label="정산 대상 금액"
                value={formatWon(s?.targetAmountPrescription)}
              />
              <AmountRow
                label="예상 금액"
                value={formatWon(s?.estimatedAmountPrescription)}
                strong
              />
            </Group>
            <Group
              head={
                <Badge state="success" size="medium">
                  정산
                </Badge>
              }
            >
              <AmountRow
                label="정산 대상 금액"
                value={
                  s?.hasSettlement ? formatWon(s.targetAmountSettlement) : '-'
                }
              />
              <AmountRow
                label="확정 금액"
                value={
                  s?.hasSettlement
                    ? formatWon(s.confirmedAmountSettlement)
                    : '-'
                }
                strong
              />
              <AmountRow
                label="정산 비율"
                value={formatRatio(s?.settlementRatio)}
                strong
              />
            </Group>
          </div>
          <div aria-hidden className="hidden w-px bg-line-grey lg:block" />
          <div className="flex min-w-0 flex-1 flex-col gap-6">
            <Group head={<Caption>직전 5개월</Caption>}>
              {comparisonRows(
                'avg',
                trend.data?.prescription,
                trend.data?.settlement,
              )}
            </Group>
            <Group head={<Caption>전 월 대비</Caption>}>
              {comparisonRows(
                'prev',
                trend.data?.prescription,
                trend.data?.settlement,
              )}
            </Group>
          </div>
        </div>
      </div>
    </section>
  );
};
